import Card from './Card';

/**
 * EmptyState — the three-state empty framework. Every list/dashboard surface
 * must pick the right one (see design plan, "Empty States" track):
 *
 *   variant="hero"    TRUE FIRST-RUN: an onboarding moment, not an apology.
 *                     Brand illustration + serif headline + ONE primary CTA.
 *   variant="inline"  PARTIAL EMPTY: data exists but this section/context is
 *                     missing something. Compact horizontal nudge band.
 *   variant="filter"  NO RESULTS: data exists but filters hide it — always
 *                     pair with a clear-filters action.
 *   (default)         Legacy centered block — existing call sites unchanged.
 *
 * Props: icon (react-icons component), tone (hex), title, subtitle, action,
 *        illustration (node — usually from Illustrations.jsx, hero only).
 */
export default function EmptyState({
  variant = 'default',
  icon: Ico,
  tone = '#4a6b3f',
  title,
  subtitle,
  action,
  illustration,
  className = '',
}) {
  if (variant === 'inline') {
    return (
      <div
        className={`flex items-center gap-3 px-4 py-3 border rounded-[12px] ${className}`}
        style={{ background: '#fdfbf6', borderColor: '#ebe3d3' }}
      >
        {Ico && (
          <div
            className="w-8 h-8 flex items-center justify-center shrink-0"
            style={{ background: `${tone}1a`, color: tone, borderRadius: 'var(--r-sm)' }}
          >
            <Ico size={16} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {title && <div className="text-[12.5px] font-semibold text-[#08111f] leading-snug">{title}</div>}
          {subtitle && <div className="text-[11.5px] text-[#7a7060] leading-snug mt-[1px]">{subtitle}</div>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    );
  }

  if (variant === 'hero') {
    return (
      <Card className={`px-6 py-12 flex flex-col items-center justify-center text-center gap-1 ${className}`} padded={false}>
        {illustration && <div className="mb-3">{illustration}</div>}
        {!illustration && Ico && (
          <div
            className="w-12 h-12 flex items-center justify-center mb-2"
            style={{ background: `${tone}1a`, color: tone, borderRadius: 'var(--r-xl)' }}
          >
            <Ico size={24} />
          </div>
        )}
        {title && (
          <div className="text-[19px] font-bold text-[#08111f]" style={{ fontFamily: 'var(--font-serif)' }}>
            {title}
          </div>
        )}
        {subtitle && <div className="text-[12.5px] text-[#7a7060] max-w-[460px] leading-relaxed">{subtitle}</div>}
        {action && <div className="mt-4">{action}</div>}
      </Card>
    );
  }

  // 'filter' intentionally shares the default layout — the distinction is the
  // caller's copy + clear-filters action, not the geometry.
  return (
    <Card className={`p-10 flex flex-col items-center justify-center text-center gap-2 ${className}`} padded={false}>
      {illustration && <div className="mb-1">{illustration}</div>}
      {!illustration && Ico && (
        <div
          className="w-11 h-11 flex items-center justify-center"
          style={{ background: `${tone}1a`, color: tone, borderRadius: 'var(--r-xl)' }}
        >
          <Ico size={22} />
        </div>
      )}
      {title && <div className="text-[13px] font-semibold text-[#08111f]">{title}</div>}
      {subtitle && <div className="text-[12px] text-[#7a7060] max-w-[420px]">{subtitle}</div>}
      {action && <div className="mt-2">{action}</div>}
    </Card>
  );
}
